import { orderBy } from "lodash";
import { AggregatPeriods, IAggregatPeriod } from "~app/common/models/aggregatperiod";
import { IDagKvantitet } from "~app/common/models/dagkvantitet";
import { DefaultForsorjningsrad, ForsorjningskallaAggregat, ForsorjningskallaTyp, IForsorjningskalla } from "~app/common/models/forsorjningskalla";
import { LeveransraderAggregat } from "~app/common/models/leveransrad";
import { ILeverantorsorderDetailRad } from "../../models/leverantorsorder-detail-model";

export class LeverantorsorderradAggregat {

    get orderradId(): number {
        return this.lor.id;
    }

    get handelssortimentsgrupp() {
        return this.lor.handelssortimentsgrupp;
    }

    get period(): IAggregatPeriod[] {
        return this.currentPeriod;
    }

    set period(period: IAggregatPeriod[]) {
        this.currentPeriod = period;
    }

    get overenskommet(): number {
        return this.lor.overenskommenKvantitetLeveransrad;
    }

    set overenskommet(value: number) {
        this.lor.overenskommenKvantitetLeveransrad = value;
    }

    get periodFrom(): Date {
        return this.lor.periodFrom;
    }

    get periodTom(): Date {
        return this.lor.periodTom;
    }

    get mattslag(): string {
        return this.lor.mattslag;
    }

    readonly forsorjningskallor: ForsorjningskallaAggregat[];

    private readonly leveransraderAggregat: LeveransraderAggregat;
    private currentPeriod: IAggregatPeriod[];

    constructor(
        private readonly lor: ILeverantorsorderDetailRad) {

        this.currentPeriod = this.getPeriods(lor.periodFrom, lor.periodTom);
        this.leveransraderAggregat = new LeveransraderAggregat(lor.levorderLeveransrader);

        const typOrder = [
            ForsorjningskallaTyp.Produktionsomrade,
            ForsorjningskallaTyp.Terminal,
            ForsorjningskallaTyp.Leverantorsorder
        ];

        this.forsorjningskallor = orderBy(
            ForsorjningskallaAggregat.createMany(lor.forsorjningskallor, this.leveransraderAggregat),
            [
                x => typOrder.indexOf(x.typ),
                x => x.namn
            ]
        );
    }

    getPeriods = (periodFrom, periodTom): IAggregatPeriod[] => {
        const defaultPeriod = {
            type: "PERIOD",
            from: periodFrom,
            to: periodTom,
            periodMonth: periodFrom
        };
        return AggregatPeriods.create("sv-SE", defaultPeriod);
    };

    sumForsorjningsrader(period: IAggregatPeriod) {
        return this.leveransraderAggregat.sumForsorjningsrad(period);
    }

    sumLeveransrad = (period: IAggregatPeriod): number => this.leveransraderAggregat.sumLeveransrad(period);

    sumTransporterat = (period: IAggregatPeriod): number => this.leveransraderAggregat.sumTransporteratLeveransrad(period);

    sumTransporteratForsorjningskallor = (period: IAggregatPeriod, forsorjningskallorIdn?: number[]): number =>
        this.leveransraderAggregat.sumTransporterat(period, forsorjningskallorIdn);

    sumPlan = (period: IAggregatPeriod): number => this.forsorjningskallor.reduce((agg, item) => agg + item.sumPlan(period), 0);

    sumBalans = (period: IAggregatPeriod): number => {
        if (!period.isCurrentlyActive) {
            return this.sumTransporterat(period) - this.sumLeveransrad(period);
        }

        return this.sumPlan(period) - this.sumLeveransrad(period);
    };

    sumTransporteratLeveransrad = (periods: IAggregatPeriod[]): number =>
        periods.reduce((agg, item) => agg + (item.isCurrentlyActive ? this.sumLeveransrad(item) : this.sumTransporterat(item)), 0);

    avvikelse = (periods: IAggregatPeriod[]): number => this.sumTransporteratLeveransrad(periods) - this.overenskommet;

    updateLeveransradKvantitet(fordeladKvantitet: IDagKvantitet[]): void {
        this.leveransraderAggregat.updateLeveransradKvantitet(fordeladKvantitet);
    }

    hasForsorjningskalla = (raw: IForsorjningskalla): boolean => this.forsorjningskallor.some(x => x.id === raw.id && x.typ === raw.group);

    addForsorjningskalla(forsorjningskalla: IForsorjningskalla, fordeladKvantitet: IDagKvantitet[]): ForsorjningskallaAggregat {
        if (this.hasForsorjningskalla(forsorjningskalla)) {
            return null;
        }
        const forsorjningsrader = fordeladKvantitet
            .map(x => new DefaultForsorjningsrad(forsorjningskalla.id, forsorjningskalla.typ, x.datum, x.kvantitet));

        const aggregat = new ForsorjningskallaAggregat(forsorjningskalla, forsorjningsrader);

        this.forsorjningskallor.push(aggregat);
        return aggregat;
    }

    removeForsorjningskalla(forsorjningskalla: ForsorjningskallaAggregat): void {
        const idx = this.forsorjningskallor.indexOf(forsorjningskalla);
        this.forsorjningskallor.splice(idx, 1);

        this.leveransraderAggregat.removeForsorjningskalla(forsorjningskalla);

        const rawIdx = this.lor.forsorjningskallor.findIndex(x => x.id === forsorjningskalla.id && x.group === forsorjningskalla.typ);
        this.lor.forsorjningskallor.splice(rawIdx, 1);
    }
}
